import { MediaUploadDto } from "collaboration-service";
import { Media } from "components/MediaSelection/MediaSelection";
import { belowBreakpointOrEqual, ButtonKind, fadeColor, getColor, Icon, ImgIcons, styled, TooltipProps } from 'imaginarity-react-ui';
import * as React from 'react';
import Dropzone from 'react-dropzone';
import { useImgI18N } from 'services/ImgI18N';
import { useAboveBreakpointOrEqual, useBelowBreakpointOrEqual } from 'services/useAppTheme';
import FileSelector, { SelectFileModuleProps } from './FileSelector';

interface UploadSelectionProps<T> {
    media: T;
    onFileSelected: (file: File | undefined, media: T) => void;
    icon?: ImgIcons;
    namespace?: string;
    container?: string;
    buttonText?: string;
    dropAreaText?: string;
    haveContentText?: string;
    noContentText?: string;
    children?: (p: SelectFileModuleProps) => JSX.Element;
    kind?: ButtonKind;
    tooltip?: Omit<TooltipProps, "children">;
}

const GridCheckboxDropzone = styled.div`
    display: grid;
    grid-template-columns: ${props => belowBreakpointOrEqual(props, "tablet") ? "1fr" : "1fr 2fr"}; 
    grid-template-rows: ${props => belowBreakpointOrEqual(props, "tablet") ? "min-content" : "min-content"}; 
    margin-bottom: 10px;
    grid-template-rows: unset; 
    -ms-grid-rows: unset;
    grid-template-columns: ${p => belowBreakpointOrEqual(p, "desktop") ? "1fr" : "1fr 1fr"};
    -ms-grid-columns: ${p => belowBreakpointOrEqual(p, "desktop") ? "1fr" : "1fr 1fr"};
`;
const InfoUploadContainer = styled.div`
    margin-top: 3px;
    width: 100%;
`;
const InfoUpload = styled.div`
    display: ${props => belowBreakpointOrEqual(props, "desktop") ? "inline-block" : "inline-block"};
    margin-top: ${props => belowBreakpointOrEqual(props, "desktop") ? "10px" : "3px"};
    line-height: ${props => belowBreakpointOrEqual(props, "desktop") ? "unset" : "16px"};
    margin-right: ${props => belowBreakpointOrEqual(props, "desktop") ? "10px" : "unset"};
    font-weight: bolder;
    font-size: ${props => belowBreakpointOrEqual(props, "desktop") ? "1em" : "0.75rem"};
`;
const InfoUploadFile = styled.div`
    display: ${props => belowBreakpointOrEqual(props, "desktop") ? "inline-block" : "block"};
    line-height: ${props => belowBreakpointOrEqual(props, "desktop") ? "unset" : "16px"};
    color: ${p => getColor(p, p.theme.colors.mainForeground)};
    font-size: ${props => belowBreakpointOrEqual(props, "desktop") ? "1em" : "0.8rem"};
    width: 100%;
    svg{
        stroke: ${p => getColor(p, p.theme.colors.mainForeground)};
    }
`;
const InfoUploadFileDelete = styled.div`
    float: right;
    margin: 0 20px 0 10px;
    cursor: pointer;
    svg{
        stroke: ${p => getColor(p, p.theme.colors.lightGrey)};
        &:hover{
            stroke: ${p => getColor(p, p.theme.colors.accentRed)};
        }
    }
`;
const InfoMediaContainer = styled.div`
    margin-top: 13px;
    color: ${p => getColor(p, p.theme.colors.lightGrey)};
    width: 100%;
`;
const DropArea = styled.div<{ hasContent: boolean, grey: boolean }>`
    padding: 9px;
    height: 80px;
    text-transform: uppercase;
    //color: ${p => getColor(p, "@lightGrey")};
    //border: 1px solid ${p => getColor(p, "@middleLightGrey")};
    //background-color: ${p => getColor(p, "@veryLightGrey")};

    background-color: ${p => p.hasContent || p.grey ? getColor(p, "@veryLightGrey") : fadeColor(p, "@accentRed", 5)};
    border:  ${p => p.hasContent || p.grey ? "1px solid" + getColor(p, "@middleLightGrey") : "2px dashed" + getColor(p, "@accent")};
    color: ${p => getColor(p, p.hasContent || p.grey ? "@lightGrey" : "@accentRed")};
    //width: 20%;
    //float: right;
    transition: all 0.2s ease-out;
    //cursor: pointer;
    svg{
        stroke: ${p => getColor(p, p.hasContent || p.grey ? "@lightGrey" : "@accentRed")};
        }
    &:hover{
        color: ${p => getColor(p, "color")};
        border:  ${p => p.hasContent || p.grey ? "1px solid" + getColor(p, "color") : "2px dashed" + getColor(p, "color")};
        background-color: ${p => getColor(p, "@veryLightGrey")};
        svg{
            stroke: ${p => getColor(p, "color")};
        }
    }
`;

const UploadSelection = <T extends Media>(p: UploadSelectionProps<T>) => {
    const { media, onFileSelected, container, namespace, buttonText, dropAreaText, haveContentText, noContentText, children, icon, tooltip, kind } = p;
    const { t } = useImgI18N(namespace ?? "general")

    const small = useBelowBreakpointOrEqual("desktop");
    const large = useAboveBreakpointOrEqual("desktop");
    const f = media?.fileName;

    const fileSelected = (file: File | undefined, media: MediaUploadDto) => {
        (media as any).file = file;
        if (container && file)
            (media as any).container = container;
        onFileSelected(file, media as any as T);
    }
    const fileDeleted = () => {
        fileSelected(undefined, {} as MediaUploadDto);
    }
    const onDropAttachment = (files: File[]) => {
        if (files.length > 0) {
            const f = files[0];
            const mediaDto = {
                changed: true,
                fileName: f.name,
                mediaType: f.type,
                reference: "",
                thumbnails: [],
                links: [{
                    ref: "self",
                    uri: URL.createObjectURL(f),
                    method: "GET"
                }]
            } as MediaUploadDto;
            fileSelected(f, mediaDto);
        }
    }
    const s = (p: SelectFileModuleProps) => children ? children(p) : <div />;
    return (
        <GridCheckboxDropzone>
            <div>
                <FileSelector tooltip={tooltip} onFileSelected={fileSelected} icon={icon ?? "add circle"} content={t(buttonText ?? "click here to select attachment")} children={s} kind={kind} />
                {f ?
                    <InfoUploadContainer>
                        <InfoUpload>
                            {t(haveContentText ?? "ready for upload: ")}
                        </InfoUpload>
                        <InfoUploadFile>
                            <Icon name="box" size={small ? "20px" : "16px"} marginTop={0} style={{ float: "left", marginRight: 5 }} />
                            {media.fileName}
                            <InfoUploadFileDelete onClick={fileDeleted}>
                                <Icon name="delete" size={small ? "20px" : "16px"} marginTop={0} />
                            </InfoUploadFileDelete>
                        </InfoUploadFile>
                    </InfoUploadContainer>
                    :
                    <InfoMediaContainer>
                        {t(noContentText ?? "you have not yet selected an attachment")}
                    </InfoMediaContainer>
                }
            </div>
            {large &&
                <Dropzone onDrop={onDropAttachment}>
                    {({ getRootProps, getInputProps }) => (
                        <section className="container">
                            <DropArea {...getRootProps({ className: 'dropzone' })} hasContent={noContentText ? true : false} grey={kind === "cancelButton"}>
                                <input {...getInputProps()} />
                                <Icon name="upload cloud" size={24} marginTop={0} style={{ float: "left", marginRight: 15 }} color="@accent" />
                                {t(dropAreaText ?? "... or drag'n'drop file here")}
                            </DropArea>
                        </section>
                    )}
                </Dropzone>
            }
        </GridCheckboxDropzone>
    );
}
export default UploadSelection;